import classNames from 'classnames';
import { StepIndicator } from 'components/2-molecules/StepIndicator';
import { ProgressBarNames } from 'constants/progressBarNames';
import { usePageParam } from 'hooks/usePageParam';
import { CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAppSelector } from 'store/hooks';
import { PageListState, setMaxUnlockedProgressBarIndex } from 'store/slices/pageListSlice';
import styles from './ProgressBar.module.scss';

export interface ProgressBarProps {
	children?: JSX.Element;
	activePages: WizardPage[];
	setActivePage: (storeIndex: number) => void;
}

type ProgressBarData = {
	value: string;
	title: string;
	id: number;
	active: boolean;
	style?: CSSProperties;
	storeIndex: number | null;
	arrayIndex: number | null;
	path: string;
	show: boolean;
};

export const ProgressBar: React.FC<ProgressBarProps> = ({ children, activePages, setActivePage }) => {
	const dispatch = useDispatch();

	const modal = useAppSelector((state) => state.modal);
	const progressBarValues = useAppSelector((state) => state.claimContent.wizardData?.abgProgressBarOrder);
	const longestProgressBarStepCategory = useAppSelector(
		(state) => state.claimContent.wizardData?.abgLongestProgressBarStepCategory
	);

	const { getPageParam, setPageParam } = usePageParam();
	const currentPath = getPageParam();
	const formattedSearchString = `/${currentPath}`;

	const [progressBarObj, setProgressBarObj] = useState<ProgressBarData[]>([]);
	const [currentStepIndex, setCurrentStepIndex] = useState<number>(-1);
	const maxUnlockedIndex = useSelector((state: { pages: PageListState }) => state.pages.maxUnlockedProgressBarIndex);

	const progressBarRef = useRef<HTMLDivElement>(null);

	/**
	 * Builds the base progress bar array from progressBarValues.
	 */
	const buildBaseProgressBar = (progressBarValues: string[]): ProgressBarData[] => {
		return progressBarValues
			.filter((key) => ProgressBarNames[key as keyof typeof ProgressBarNames])
			.map((key, arrayIndex) => ({
				value: key,
				title: ProgressBarNames[key as keyof typeof ProgressBarNames],
				id: arrayIndex,
				active: false,
				style: { fontWeight: 'normal' },
				storeIndex: null,
				arrayIndex: null,
				path: '',
				show: true,
			}));
	};

	/**
	 * Updates progress bar properties based on activePages and currentPath.
	 */
	const updateProgressBarProperties = (
		baseArr: ProgressBarData[],
		activePages: WizardPage[],
		formattedSearchString: string
	): ProgressBarData[] => {
		return baseArr.map((item, arrayIndex) => {
			const currentPageInGroup =
				activePages.find((p) => p.pageCategories.value === item.value && p.path === formattedSearchString) ||
				activePages.find((p) => p.pageCategories.value === item.value);

			const storeIndex = currentPageInGroup
				? activePages.findIndex((p) => p.path === currentPageInGroup.path)
				: -1;

			const isActive =
				currentPageInGroup?.path === formattedSearchString &&
				currentPageInGroup?.pageCategories.value === item.value;

			return {
				...item,
				storeIndex,
				arrayIndex,
				active: isActive,
				style: { fontWeight: isActive ? 'bold' : 'normal' },
				path: currentPageInGroup?.path ?? '',
			};
		});
	};

	/**
	 * Compares two progress bar arrays for changes.
	 */
	const hasProgressBarChanged = (newProgressBar: ProgressBarData[], oldProgressBar: ProgressBarData[]): boolean => {
		if (!newProgressBar || !oldProgressBar) return true;

		if (newProgressBar.length !== oldProgressBar.length) return true;

		const serializeProgressBar = (progressBar: ProgressBarData[]) =>
			progressBar.map(({ value, active, storeIndex, path }) => ({ value, active, storeIndex, path }));

		return (
			JSON.stringify(serializeProgressBar(newProgressBar)) !==
			JSON.stringify(serializeProgressBar(oldProgressBar))
		);
	};

	/**
	 * Navigate to a specific section by array index.
	 */
	const goToSection = (
		arrayIndex: number | null | undefined,
		event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>
	) => {
		if (arrayIndex == null || arrayIndex < 0 || arrayIndex > maxUnlockedIndex) return;

		setProgressBarObj((prev) =>
			prev.map((data, i) => ({
				...data,
				style: { fontWeight: i === arrayIndex ? 'bold' : 'normal' },
			}))
		);

		const { storeIndex, path } = progressBarObj[arrayIndex];
		if (storeIndex != null && storeIndex >= 0) {
			setActivePage(storeIndex);
		}
		if (path) {
			setPageParam(path.replace(/^\//, ''));
		}

		setCurrentStepIndex(arrayIndex);
		if (arrayIndex > maxUnlockedIndex) {
			setMaxUnlockedProgressBarIndex(arrayIndex);
		}
	};

	/**
	 * Calculates the index of the longest step in the progress bar.
	 */
	const longestStepIndex = useMemo(() => {
		if (!progressBarValues?.length || !longestProgressBarStepCategory) return -1;

		const filteredProgressBarValues = progressBarValues.filter(
			(key) => ProgressBarNames[key as keyof typeof ProgressBarNames]
		);

		return filteredProgressBarValues.findIndex((value) => value === longestProgressBarStepCategory);
	}, [progressBarValues, longestProgressBarStepCategory]);

	useEffect(() => {
		if (!progressBarValues?.length || !activePages?.length) return;

		const baseArr = buildBaseProgressBar(progressBarValues);
		const updatedProgressBar = updateProgressBarProperties(baseArr, activePages, formattedSearchString);

		if (hasProgressBarChanged(updatedProgressBar, progressBarObj)) {
			setProgressBarObj(updatedProgressBar);
		}

		const idx = updatedProgressBar.findIndex((p) => p.active);
		if (idx >= 0) {
			setCurrentStepIndex(idx);
			if (idx > maxUnlockedIndex) {
				dispatch(setMaxUnlockedProgressBarIndex(idx)); // Persist in ReduxState
			}
		}
	}, [progressBarValues, activePages, currentPath, maxUnlockedIndex, progressBarObj]);

	return (
		<div
			ref={progressBarRef}
			className={styles.ProgressBar__wrapper}
		>
			<div className={classNames(styles.ProgressBar, modal.isModal && styles.ProgressBar___isModal)}>
				<StepIndicator
					steps={
						progressBarObj?.map(({ title, value, arrayIndex }) => ({
							title,
							value,
							index: arrayIndex,
						})) || []
					}
					currentStepIndex={currentStepIndex}
					getLiIndex={goToSection}
					longSteps={[longestStepIndex]}
				/>
			</div>
		</div>
	);
};
